import React, { useEffect, useMemo, useState } from 'react';

import { TranscriptChapter } from './transcriptHelpers';

type Params = {
  chapters: TranscriptChapter[];
  playedMs: number;
  entriesContainerRef: React.RefObject<HTMLDivElement | null>;
  chipContainerRef: React.RefObject<HTMLDivElement | null>;
  chipRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
};

/**
 * Derives the active line/chapter from playback position, keeps the active
 * chapter chip scrolled into view, and reports whether the floating "Sync to
 * video" pill should show (active line scrolled off-screen).
 */
export function usePlaybackSync({
  chapters,
  playedMs,
  entriesContainerRef,
  chipContainerRef,
  chipRefs,
}: Params) {
  const [isActiveLineVisible, setIsActiveLineVisible] = useState(true);

  // Active line/chapter is derived from playback position (entries are
  // assumed sorted ascending by start time).
  const flatEntries = useMemo(
    () =>
      chapters.flatMap((chapter, chapterIndex) =>
        chapter.entries.map((entry) => ({ chapterIndex, entry })),
      ),
    [chapters],
  );

  const activeFlatEntry = useMemo(() => {
    let active: (typeof flatEntries)[number] | undefined;
    for (const flatEntry of flatEntries) {
      if (flatEntry.entry.start <= playedMs) active = flatEntry;
      else break;
    }
    return active;
  }, [flatEntries, playedMs]);

  const activeChapterIndex = activeFlatEntry?.chapterIndex ?? -1;

  // Automatically scroll the active chapter button chip into view horizontally
  useEffect(() => {
    const container = chipContainerRef.current;
    const activeChip = chipRefs.current[activeChapterIndex];

    if (container && activeChip) {
      const containerWidth = container.clientWidth;
      const chipWidth = activeChip.clientWidth;

      const containerLeft = container.getBoundingClientRect().left;
      const chipLeft = activeChip.getBoundingClientRect().left;

      // Center the active chip in the horizontal track
      const scrollTarget =
        chipLeft -
        containerLeft +
        container.scrollLeft -
        containerWidth / 2 +
        chipWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollTarget),
        behavior: 'smooth',
      });
    }
  }, [activeChapterIndex, chipContainerRef, chipRefs]);

  const getActiveLineEl = () =>
    entriesContainerRef.current?.querySelector(
      '[data-testid="captions-entry"][aria-current="true"]',
    );

  // Tracks whether the active line is on-screen, so the "Sync to video" pill
  // only shows once the user scrolls past it.
  useEffect(() => {
    const activeEl = getActiveLineEl();

    if (!activeEl) {
      setIsActiveLineVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsActiveLineVisible(entry.isIntersecting),
      { root: null, threshold: 0 },
    );
    observer.observe(activeEl);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlatEntry]);

  const isSyncPillVisible = !!activeFlatEntry && !isActiveLineVisible;

  const handleSyncToVideo = () => {
    getActiveLineEl()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return {
    activeEntry: activeFlatEntry?.entry,
    activeChapterIndex,
    isSyncPillVisible,
    handleSyncToVideo,
  };
}
